import { useEffect, useRef, useState } from 'react';
import useTranslation from 'next-translate/useTranslation';

// One question on the homepage, answered anonymously, logged as umami events.
//
// This exists because analytics can say how many people visit and roughly when,
// but not who they are. The current question asks why people travel, the first
// split any travel advertiser asks for (work travellers are the lounge and
// fast-track audience, holiday travellers the eSIM, insurance and parking one).
//
// Deliberately not a poll framework. It is one question; rotating to the next
// one means editing this file and picking a new event name, which keeps each
// question's answers in their own bucket in umami rather than smeared across a
// shared one. Retired questions keep their answers in umami under their own
// event: `poll-transport` (how people get to the airport, 2026-09-03 to
// 2026-10-25).
//
// Solid buttons rather than outlined ones. Outlined read as too quiet to tap,
// and an unanswered question is worth nothing. Three alternatives (outlined,
// list rows, tinted chips) were compared on the real page and are kept on the
// prototype/poll-button-styles branch; revisit them once there is a response
// rate to compare against.
const EVENT = 'poll-trip-purpose';
// A new key on purpose: people who answered the transport question get asked
// this one too.
const STORAGE_KEY = 'cph-poll-trip-purpose';

// Values are stable identifiers, not labels: they end up in umami and must not
// change when the Danish or English wording does.
const ANSWERS = [
    { value: 'holiday', labelKey: 'poll_purpose_holiday' },
    { value: 'work', labelKey: 'poll_purpose_work' },
    { value: 'visiting', labelKey: 'poll_purpose_visiting' },
    { value: 'other', labelKey: 'poll_purpose_other' }
];

// Private mode and blocked site data make these throw rather than return null,
// so every access is guarded. Failing open (ask again) beats failing closed
// (never ask), since the worst case is one extra question.
const hasAnswered = () => {
    try {
        return window.localStorage.getItem(STORAGE_KEY) !== null;
    } catch {
        return false;
    }
};
const remember = (value) => {
    try {
        window.localStorage.setItem(STORAGE_KEY, value);
    } catch {
        /* answer still counted, we just may ask this device again */
    }
};

export default function TravelPoll() {
    const { t, lang } = useTranslation('home');
    const [state, setState] = useState('hidden');
    const shownTracked = useRef(false);

    useEffect(() => {
        // localStorage is client-only, so the server cannot know whether to
        // render this. Deciding after mount avoids a hydration mismatch, the
        // same reason SecurityOpeningNote waits.
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setState(hasAnswered() ? 'hidden' : 'asking');
    }, []);

    useEffect(() => {
        // The denominator. Without it, "62% drive" is unreadable: it could be
        // 62% of everyone or 62% of the handful who bothered to answer, and
        // self-selection is the first thing anyone will challenge.
        if (state === 'asking' && !shownTracked.current) {
            shownTracked.current = true;
            if (typeof umami !== 'undefined') {
                umami.track(`${EVENT}-shown`, { locale: lang });
            }
        }
    }, [state, lang]);

    if (state === 'hidden') return null;

    const answer = (value) => {
        if (typeof umami !== 'undefined') {
            umami.track(EVENT, { answer: value, locale: lang });
        }
        remember(value);
        setState('answered');
    };

    return (
        <div className="mx-auto my-5 max-w-md rounded-lg bg-white p-4 text-left text-gray-800 shadow-lg">
            {state === 'answered' ? (
                <p className="text-sm">{t('poll_thanks')}</p>
            ) : (
                <>
                    <p className="font-bold">{t('poll_purpose_question')}</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                        {ANSWERS.map(({ value, labelKey }) => (
                            <button
                                key={value}
                                type="button"
                                onClick={() => answer(value)}
                                className="rounded-full bg-[var(--secondary)] px-4 py-2 text-sm font-medium text-white transition hover:bg-[var(--primary)]"
                            >
                                {t(labelKey)}
                            </button>
                        ))}
                    </div>
                    <p className="mt-3 text-xs text-gray-500">
                        {t('poll_note')}
                    </p>
                </>
            )}
        </div>
    );
}
